import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime } from 'rxjs';
import { formatarData } from 'src/app/core/utils/utilities';
import { exportTablePdf } from 'src/app/core/utils/export-table-pdf';
import { AppExportBase64 } from 'src/app/services/ExportBase64/exportBase64.service';
import { EstoqueService } from 'src/app/services/EstoqueService/estoque.service';
import { IEstoqueItem } from 'src/app/services/EstoqueService/estoque.model';
import {
  labelStatusProduto,
  labelUnidadeMedida
} from '../estoque.constants';

@Component({
  selector: 'app-listagem-estoque',
  templateUrl: './listagem-estoque.component.html',
  styleUrls: ['./listagem-estoque.component.scss']
})
export class ListagemEstoqueComponent implements OnInit, AfterViewInit {
  form = this.fb.group({
    NM_PROD: [''],
    FL_ESTQ_BX: [false],
    FL_EM_ESTQ: [false],
    PAGE_NUMBER: [1],
    PAGE_SIZE: [8]
  });

  isLoading = false;
  tableData: Array<any> = [];
  maxPages = 1;
  valorTotalEstoque = 0;
  expandirCardsMobile = true;

  tableColumns = [
    { header: 'Produto', key: 'produto', type: 'text' },
    { header: 'Saldo', key: 'saldo', type: 'text' },
    { header: 'Mínimo', key: 'minimo', type: 'text' },
    { header: 'Valor Unit.', key: 'valorUnit', type: 'text' },
    { header: 'Valor Total', key: 'valorTotal', type: 'text' },
    { header: 'Unidade', key: 'unidade', type: 'text' },
    { header: 'Status', key: 'status', type: 'text' },
    { header: 'Última Mov.', key: 'ultimaMov', type: 'text' },
    { header: 'Estoque Baixo', key: 'estoqueBaixo', type: 'text' }
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private estoqueService: EstoqueService,
    private appExportBase64: AppExportBase64
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => this.loadData());
  }

  navegarEntrada(): void {
    this.router.navigate(['cadastro/estoque/entrada']);
  }

  navegarSaida(): void {
    this.router.navigate(['cadastro/estoque/saida']);
  }

  mudarPagina(page: number): void {
    this.form.controls.PAGE_NUMBER.setValue(page + 1);
    this.loadData();
  }

  toggleEstoqueBaixo(value: boolean): void {
    this.form.controls.FL_ESTQ_BX.setValue(value);
  }

  toggleEmEstoque(value: boolean): void {
    this.form.controls.FL_EM_ESTQ.setValue(value);
  }

  get valorTotalFormatado(): string {
    return this.valorTotalEstoque.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  private buildFilterParams() {
    const flEstqBx = this.form.controls.FL_ESTQ_BX.value;
    const flEmEstq = this.form.controls.FL_EM_ESTQ.value;
    return {
      NM_PROD: this.form.controls.NM_PROD.value || undefined,
      FL_ESTQ_BX: flEstqBx ? true : undefined,
      FL_EM_ESTQ: flEmEstq ? true : undefined
    };
  }

  loadData(): void {
    this.isLoading = true;
    this.estoqueService
      .GetEstoquePaginated({
        ...this.buildFilterParams(),
        PAGE_NUMBER: this.form.controls.PAGE_NUMBER.value ?? 1,
        PAGE_SIZE: this.form.controls.PAGE_SIZE.value ?? 8
      })
      .subscribe({
        next: response => {
          this.valorTotalEstoque = Number(response.VL_ESTQ_TOTL ?? 0);
          this.tableData = (response.ITEMS ?? []).map((item: IEstoqueItem) => ({
            produto: item.NM_PROD,
            saldo: item.QT_SALD,
            minimo: item.QT_ESTQ_MIN,
            valorUnit: Number(item.VL_UNIT).toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL'
            }),
            valorTotal: (item.QT_SALD * item.VL_UNIT).toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL'
            }),
            unidade: labelUnidadeMedida(item.NU_UNID_MED),
            status: labelStatusProduto(item.NU_STTS),
            ultimaMov: item.DT_ULTM_MOVM ? formatarData(item.DT_ULTM_MOVM) : '---',
            estoqueBaixo: item.FL_ESTQ_BX ? 'Sim' : 'Não'
          }));
          this.maxPages = response.TOTAL_PAGES ?? 1;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  exportarExcel(): void {
    this.isLoading = true;
    this.estoqueService
      .GetReportExcel({
        ReportType: 'estoque',
        ...this.buildFilterParams()
      })
      .subscribe({
        next: response => {
          this.appExportBase64.downloadExcel(response.ReportBase64, 'estoque-atual');
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  exportarPdf(): void {
    exportTablePdf(
      'Saldo em Estoque',
      [
        { header: 'Produto', key: 'produto', width: 160 },
        { header: 'Saldo', key: 'saldo', width: 60 },
        { header: 'Mínimo', key: 'minimo', width: 60 },
        { header: 'Vl Unit.', key: 'valorUnit', width: 90 },
        { header: 'Vl Total', key: 'valorTotal', width: 90 },
        { header: 'Unidade', key: 'unidade', width: 70 },
        { header: 'Status', key: 'status', width: 70 },
        { header: 'Última Mov.', key: 'ultimaMov', width: 110 },
        { header: 'Baixo', key: 'estoqueBaixo', width: 50 }
      ],
      this.tableData,
      'estoque-atual',
      `Valor total em estoque: ${this.valorTotalFormatado}`
    );
  }
}
